import { useCallback, useRef, useState } from "react";
import { Package, Truck, MapPin, Box, Zap } from "lucide-react";

const COURIER_NODES = [
  { id: "courier-node-express", name: "BlueDart", meta: "Air · 24–48h PAN India", logo: "bluedart" },
  { id: "courier-node-surface", name: "Delhivery", meta: "Surface · 2–4 days", logo: "delhivery" },
  { id: "courier-node-cod", name: "XpressBees", meta: "COD + remittance", logo: "xpressbees" },
  { id: "courier-node-b2b", name: "DTDC", meta: "Heavy & bulk loads", logo: "dtdc" },
  { id: "courier-node-international", name: "DHL", meta: "190+ destinations", logo: "dhl" },
  { id: "courier-node-hyperlocal", name: "Ekart", meta: "Same-day city delivery", logo: "ekart" },
];
const LIFECYCLE = ["Rate", "Route", "Pickup", "Transit", "Delivery"];
const ORBIT_ICONS = [Package, Box, Truck, MapPin, Package, Box];

function polarStyle(angleDeg, radiusPct) {
  return {
    left: "50%", top: "50%",
    transform: `translate(-50%, -50%) rotate(${angleDeg}deg) translate(${radiusPct}px) rotate(${-angleDeg}deg)`,
  };
}

export default function HeroWheel({ compact = false }) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: x * 14, y: y * 14 });
  }, []);

  const size = compact ? "w-[300px] sm:w-[380px]" : "w-[320px] sm:w-[460px] lg:w-[600px] xl:w-[660px]";

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      className={`relative ${size} aspect-square select-none`}
      style={{ perspective: "1100px" }}
      data-testid="hero-logistics-wheel"
      aria-hidden="true"
    >
      <div
        className="absolute inset-0 transition-transform duration-300 ease-out"
        style={{ transform: `translate3d(${tilt.x}px, ${tilt.y}px, 0) rotateX(${-tilt.y * 0.35}deg) rotateY(${tilt.x * 0.35}deg)` }}
      >
        {/* glowing route arcs */}
        <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full opacity-70">
          <defs>
            <linearGradient id="routeGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF9933" />
              <stop offset="55%" stopColor="#12C8F5" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(18,200,245,0.14)" strokeWidth="0.35" />
          <circle cx="50" cy="50" r="36" fill="none" stroke="rgba(255,153,51,0.16)" strokeWidth="0.35" />
          <circle cx="50" cy="50" r="24" fill="none" stroke="rgba(16,185,129,0.18)" strokeWidth="0.35" />
          <path d="M 8 62 Q 30 18 62 12 T 92 46" fill="none" stroke="url(#routeGrad)" strokeWidth="0.55" className="route-dash" />
          <path d="M 14 30 Q 48 66 88 70" fill="none" stroke="rgba(18,200,245,0.6)" strokeWidth="0.45" className="route-dash-fast" />
          <path d="M 26 90 Q 50 60 76 24" fill="none" stroke="rgba(255,153,51,0.5)" strokeWidth="0.45" className="route-dash" style={{ animationDuration: "9s" }} />
        </svg>

        {/* OUTER RING — courier partner nodes */}
        <div className="absolute inset-0 wheel-spin" style={{ "--wheel-dur": "95s" }}>
          {COURIER_NODES.map((c, i) => (
            <OuterNode key={c.id} courier={c} index={i} compact={compact} />
          ))}
        </div>

        {/* MIDDLE RING — lifecycle */}
        <div className="absolute inset-0 wheel-spin-rev" style={{ "--wheel-dur": "130s" }}>
          {LIFECYCLE.map((s, i) => (
            <LifecycleChip key={s} label={s} index={i} compact={compact} />
          ))}
        </div>

        {/* orbiting packages */}
        {[36, 24, 47].map((r, i) => (
          <div key={r} className="absolute inset-0 wheel-spin" style={{ "--wheel-dur": `${22 + i * 14}s` }}>
            {[0, 1].map((k) => {
              const Icon = ORBIT_ICONS[(i * 2 + k) % ORBIT_ICONS.length];
              return (
                <span key={k} className="absolute" style={polarStyle(k * 180 + i * 55, 0)}>
                  <span
                    className="wheel-spin-rev flex items-center justify-center rounded-md border border-shipany-cyan/40 bg-[#0B1E3D]/90 shadow-[0_0_14px_rgba(18,200,245,0.45)]"
                    style={{ "--wheel-dur": `${22 + i * 14}s`, width: compact ? 16 : 22, height: compact ? 16 : 22, transform: `translate(${r * (compact ? 1.55 : 3.1)}px, 0)` }}
                  >
                    <Icon className={compact ? "h-2 w-2 text-shipany-cyan" : "h-3 w-3 text-shipany-cyan"} />
                  </span>
                </span>
              );
            })}
          </div>
        ))}

        {/* route particles */}
        {Array.from({ length: compact ? 6 : 12 }).map((_, i) => (
          <span key={i} className="particle absolute rounded-full bg-shipany-cyan"
            style={{
              width: i % 3 === 0 ? 3 : 2, height: i % 3 === 0 ? 3 : 2,
              left: `${12 + (i * 71) % 76}%`, top: `${58 + (i * 13) % 34}%`,
              "--p-dur": `${5 + (i % 5)}s`, "--p-delay": `${(i * 0.9) % 6}s`,
            }} />
        ))}

        {/* INNER RING — SHIPANY core */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="node-glow relative flex items-center justify-center rounded-full border border-shipany-cyan/40 bg-[#071B3A]/95 shadow-2xl"
            style={{ width: compact ? 92 : 132, height: compact ? 92 : 132 }}>
            <div className="absolute inset-2 rounded-full border border-dashed border-shipany-cyan/25 wheel-spin" style={{ "--wheel-dur": "36s" }} />
            <div className="text-center">
              <Zap className={`mx-auto text-[#FF9933] ${compact ? "h-4 w-4" : "h-6 w-6"}`} />
              <p className={`font-display font-extrabold tracking-tight text-white ${compact ? "text-sm" : "text-lg"}`}>SHIPANY</p>
              <p className={`mono uppercase tracking-[0.2em] text-shipany-cyan ${compact ? "text-[7px]" : "text-[9px]"}`}>Smart Engine</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OuterNode({ courier, index, compact }) {
  // place on outer circle using % of container
  const angle = index * 60 * (Math.PI / 180);
  const r = compact ? 38 : 44; // percent
  const x = 50 + r * Math.sin(angle);
  const y = 50 - r * Math.cos(angle);
  return (
    <div className="group absolute" style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%,-50%)" }}>
      <div className="wheel-spin-rev" style={{ "--wheel-dur": "95s" }}>
        <div data-testid={courier.id}
          className="relative flex cursor-pointer items-center justify-center rounded-xl border border-white/30 bg-white/95 p-1.5 shadow-lg backdrop-blur transition-all duration-300 group-hover:border-[#FF9933] group-hover:shadow-[0_0_22px_rgba(255,153,51,0.45)]"
          style={{ width: compact ? 40 : 56, height: compact ? 40 : 56 }}>
          <img src={`/courier-logos/${courier.logo}.png`} alt={courier.name} className="max-h-full max-w-full object-contain" />
        </div>
        <div className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 w-36 -translate-x-1/2 rounded-xl border border-white/10 bg-[#030B18]/95 p-2.5 opacity-0 shadow-xl backdrop-blur transition-all duration-200 group-hover:opacity-100">
          <p className="text-[11px] font-bold text-white">{courier.name}</p>
          <p className="mono mt-0.5 text-[9px] text-shipany-cyan">{courier.meta}</p>
        </div>
      </div>
    </div>
  );
}

function LifecycleChip({ label, index, compact }) {
  const angle = index * 72 * (Math.PI / 180);
  const r = compact ? 26 : 29;
  const x = 50 + r * Math.sin(angle);
  const y = 50 - r * Math.cos(angle);
  return (
    <div className="absolute" style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%,-50%)" }}>
      <span className="wheel-spin block rounded-full border border-[#FF9933]/40 bg-[#071B3A]/90 font-semibold uppercase tracking-wider text-[#FFB25E]"
        style={{ "--wheel-dur": "130s", fontSize: compact ? 7 : 9, padding: compact ? "2px 6px" : "3px 9px" }}>
        {label}
      </span>
    </div>
  );
}
